/**
 * Call pool — orchestration de plusieurs callMachine en parallèle.
 *
 * Une instance par caller. Le pool maintient un Map<callId, ActorRef> et
 * expose des helpers pour spawn / send / getActiveMachine.
 *
 * Voir docs/refactor/call-state-machine.md §2 pour l'invariant "un seul
 * connected_to_agent à la fois".
 */

import { createActor, type ActorRefFrom } from 'xstate';
import { callMachine, type CallMachineInput } from './callMachine';

export type CallActor = ActorRefFrom<typeof callMachine>;

/** Bag d'overrides actions injectées dans la machine au spawn. */
export type CallActionOverrides = Record<string, (args: unknown) => void>;

export interface CallPool {
  spawn(input: CallMachineInput): CallActor;
  get(callId: string): CallActor | undefined;
  remove(callId: string): void;
  list(): CallActor[];
  /** La machine actuellement en bridging | connected_to_agent | qualified. */
  getActiveMachine(): CallActor | undefined;
  /** Force STOP_ALL sur toutes les machines non terminales. */
  stopAll(): void;
  /** Subscribe à tout changement (ajout, retrait, transition d'une machine). */
  subscribe(listener: () => void): () => void;
  /** Permet de remplacer les actions injectées (utilisé par DialerContext au mount). */
  setActions(actions: CallActionOverrides | null): void;
}

const NON_TERMINAL_STATES = new Set([
  'dialing',
  'ringing',
  'bridging',
  'connected_to_agent',
  'qualified',
  // 'voicemail' a un after qui mène à 'completed' ; on le considère non-terminal
  // pendant ses 300ms, mais le pool ne le compte pas comme actif pour le refill.
]);

const SHEET_OPEN_STATES = new Set(['bridging', 'connected_to_agent', 'qualified']);

export function createCallPool(): CallPool {
  const actors = new Map<string, CallActor>();
  const listeners = new Set<() => void>();
  let injectedActions: CallActionOverrides | null = null;

  const notify = () => {
    for (const l of listeners) l();
  };

  return {
    setActions(actions) {
      injectedActions = actions;
    },

    spawn(input) {
      const existing = actors.get(input.callId);
      if (existing) {
        // eslint-disable-next-line no-console
        console.warn('[callPool] spawn called for existing callId, returning existing', input.callId);
        return existing;
      }
      const provided = injectedActions
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (callMachine.provide({ actions: injectedActions as any }) as typeof callMachine)
        : callMachine;
      const actor = createActor(provided, { input });
      actor.subscribe(() => notify());
      actor.subscribe({
        complete: () => {
          actors.delete(input.callId);
          notify();
        },
      });
      actor.start();
      actors.set(input.callId, actor);
      notify();
      return actor;
    },

    get(callId) {
      return actors.get(callId);
    },

    remove(callId) {
      const a = actors.get(callId);
      if (!a) return;
      a.stop();
      actors.delete(callId);
      notify();
    },

    list() {
      return Array.from(actors.values());
    },

    getActiveMachine() {
      for (const a of actors.values()) {
        const snap = a.getSnapshot();
        const stateValue: string = typeof snap.value === 'string' ? snap.value : (Object.keys(snap.value)[0] ?? '');
        if (SHEET_OPEN_STATES.has(stateValue)) return a;
      }
      return undefined;
    },

    stopAll() {
      for (const a of actors.values()) {
        const snap = a.getSnapshot();
        const stateValue: string = typeof snap.value === 'string' ? snap.value : (Object.keys(snap.value)[0] ?? '');
        if (NON_TERMINAL_STATES.has(stateValue)) {
          a.send({ type: 'STOP_ALL' });
        }
      }
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * RUI-P3 — tells the call's machine that its qualification was persisted,
 * so it leaves `qualified` (and the bar leaves its "live" mode). Before this,
 * nothing emitted QUALIFICATION_SAVED and actors stayed in `qualified`.
 * Safe when the call has no actor or is in another state (xstate ignores it).
 */
export function emitQualificationSaved(
  pool: Pick<CallPool, 'get'> | null | undefined,
  callId: string,
  data: { call_outcome?: string | null; call_notes?: string | null } | null | undefined,
): boolean {
  const actor = pool?.get(callId);
  if (!actor) return false;
  actor.send({ type: 'QUALIFICATION_SAVED', outcome: data?.call_outcome || '', notes: data?.call_notes || '' });
  return true;
}
